import { Box, Flex } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AuthContext } from '../../hooks/auth/useAuth'
import { useCurrentSelf } from '../../hooks/auth/useCurrentSelf'
import { useRole } from '../../hooks/auth/useRole'
import { useRouteReadyMetric } from '../../lib/performance/monitoring'
import Loading from '../../pages/Loading'
import NavigationBar from '../Navbar'
import PageHeader from '../PageHeader'
import GlobalSearch from '../GlobalSearch'
import HRChatWidget from '../hr-chat/HRChatWidget'
import { myPermissionsQueryOptions } from '../../lib/performance/queryOptions'
import { getSubscription } from '../../api/billing'
import { expandPlanningTabs } from '../../lib/navigation/navigationConfig'
import { TabNotificationProvider } from '../../contexts/TabNotificationContext'

const CONTENT_AREA_BG = 'linear-gradient(135deg, #EEEDF6 0%, #F5F4F8 40%, #F8F4F4 70%, #FBF0F0 100%)'
const MotionBox = motion(Box)

const UserWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const { t } = useTranslation()
  const { role, isAdmin, isSuperAdmin, isOrgAdmin, isFinance } = useRole()

  const { data: myPermissions, isLoading: isPermissionsLoading } = useQuery(
    myPermissionsQueryOptions,
  )

  const { data: subscription, isLoading: isSubscriptionLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscription,
    staleTime: 5 * 60 * 1000,
    enabled: !!(auth?.isAuthenticated && auth?.isVerified),
  })

  const hasPaidSubscription = !!subscription?.hasSubscription || !!subscription?.billingExempt || isSuperAdmin
  const hasAssessmentsFeature =
    isSubscriptionLoading ||
    isSuperAdmin ||
    !!subscription?.billingExempt ||
    (!!subscription?.hasSubscription && !!subscription?.planDetails?.features.includes('assessments'))

  const isFreeRoute =
    location.pathname.startsWith('/user/home') ||
    location.pathname.startsWith('/user/todo') ||
    location.pathname.startsWith('/user/settings') ||
    location.pathname.startsWith('/user/profile')

  const hasWorkforceAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.workforce?.length ?? 0) > 0
  const hasInflowAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.inflow?.length ?? 0) > 0
  const hasExploreAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.explore?.length ?? 0) > 0
  const hasLibraryAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.library?.length ?? 0) > 0
  const hasHRChatAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.support?.includes('hr-chat') ?? false)

  const isWorkforceRoute = location.pathname.startsWith('/user/workforce')
  const isPayrollRoute = location.pathname.startsWith('/user/payroll')
  const isClassificationRoute = location.pathname.startsWith('/user/classification')
  const isOrganizationRoute = location.pathname.startsWith('/user/organization')
  const isExploreRoute = location.pathname.startsWith('/user/explore') || location.pathname.startsWith('/user/content')
  const isContentAuthoringRoute =
    location.pathname === '/user/content/new' || /^\/user\/content\/[^/]+\/edit$/.test(location.pathname)
  const isSettingsRoute = location.pathname.startsWith('/user/settings')
  const isPlanningRoute = location.pathname.startsWith('/user/planning')
  const isInflowRoute = location.pathname.startsWith('/user/inflow')
  const isOutflowRoute = location.pathname.startsWith('/user/outflow')
  const isAnalyticsRoute = location.pathname.startsWith('/user/analytics')
  const isAssignRoute = location.pathname.startsWith('/user/assign')
  const isAssessmentsRouteFamily = location.pathname.startsWith('/user/assessments')
  // Page permissions govern the shared overview only. Assigned employees can
  // still open their own take/report links, while the API enforces ownership.
  const isAssessmentsListRoute = /^\/user\/assessments\/?$/.test(location.pathname)
  const isLibraryRoute = location.pathname.startsWith('/user/library')
  const isFaqRoute = location.pathname.startsWith('/user/faq')
  const isHRChatRoute = location.pathname.startsWith('/user/hr-chat')
  const isPathsRoute = /^\/user\/explore\/paths\/[^/]+$/.test(location.pathname)
  const showChrome = !isPathsRoute
  const isPermissionGuardedRoute = isWorkforceRoute || isInflowRoute || isExploreRoute || isLibraryRoute || isHRChatRoute || isAssessmentsListRoute
  useRouteReadyMetric(location.pathname)

  const getTabConfig = () => {
    const filterTabsByPermissions = (
      section: 'planning' | 'workforce' | 'inflow' | 'explore' | 'library',
      tabs: { title: string; path: string; tab: string }[],
    ) => {
      if (isPermissionsLoading) return []

      const rawAllowedTabs = myPermissions?.[section] || []
      // Legacy: the Library tab was merged into the Content (explore) tab; old
      // 'library' grants keep working until orgs re-save their module settings.
      const allowedTabs =
        section === 'explore' ? rawAllowedTabs.map((tab) => (tab === 'library' ? 'explore' : tab)) : rawAllowedTabs
      if (section !== 'planning') {
        if (isOrgAdmin || isSuperAdmin) return tabs
        return tabs.filter((tab) => allowedTabs.includes(tab.tab))
      }

      const effectiveTabs = expandPlanningTabs(allowedTabs)
      return tabs.filter((tab) => effectiveTabs.has(tab.tab))
    }

    if (isWorkforceRoute) {
      const canSeeMutations = role === 'country_admin' || isOrgAdmin || isSuperAdmin || isFinance
      const workforceTabs = [
        { title: t('workforce.tabs.people'), path: '/user/workforce/people', tab: 'people' },
        { title: t('workforce.tabs.teams'), path: '/user/workforce/teams', tab: 'teams' },
        { title: t('workforce.tabs.organization'), path: '/user/workforce/organization', tab: 'organization' },
        { title: t('workforce.tabs.assets'), path: '/user/workforce/assets', tab: 'assets' },
        ...(canSeeMutations ? [{ title: t('workforce.tabs.mutations'), path: '/user/workforce/mutations', tab: 'mutations' }] : []),
      ]
      return filterTabsByPermissions('workforce', workforceTabs)
    }
    if (isClassificationRoute) {
      return [
        { title: t('classification.tabs.jobProfile'), path: '/user/classification/jobprofile', tab: 'jobprofile' },
        { title: t('classification.tabs.skills'), path: '/user/classification/skills', tab: 'skills' },
        { title: t('classification.tabs.competences'), path: '/user/classification/competences', tab: 'competences' },
        { title: t('classification.tabs.tasks'), path: '/user/classification/tasks', tab: 'tasks' },
        { title: t('classification.tabs.roles'), path: '/user/classification/roles', tab: 'roles' },
        { title: t('classification.tabs.accountabilityAreas'), path: '/user/classification/accountability', tab: 'accountability' },
      ]
    }
    if (isOrganizationRoute) {
      return [
        { title: t('organization.tabs.myTeams'), path: '/user/organization', tab: 'myTeams' },
        { title: t('organization.tabs.myOrganization'), path: '/user/organization/organization', tab: 'organization' },
      ]
    }
    if (isContentAuthoringRoute) {
      return null
    }
    if (isExploreRoute && showChrome) {
      // Assign + Statistics moved out into their own Assign page (isAssignRoute below).
      // Paths tab removed — it duplicated the Content nav's Paths tab (same
      // PathTable/UserPathTable), which is unrestricted and always reachable.
      // Assessments moved out to its own Manage entry, so a single remaining
      // tab would be a tab row of one.
      const exploreTabs = [
        { title: t('explore.tabs.explore'), path: '/user/explore/content', tab: 'explore' },
      ]
      return filterTabsByPermissions('explore', exploreTabs)
    }
    if (isAssignRoute) {
      return [
        { title: t('assign.tabs.document'), path: '/user/assign/document', tab: 'document' },
        { title: t('assign.tabs.learning'), path: '/user/assign/learning', tab: 'learning' },
        { title: t('assign.tabs.survey'), path: '/user/assign/survey', tab: 'survey' },
        { title: t('assign.tabs.path'), path: '/user/assign/path', tab: 'path' },
        { title: t('assign.tabs.statistics'), path: '/user/assign/statistics', tab: 'statistics' },
      ]
    }
    if (isLibraryRoute) {
      const libraryTabs = [
        { title: t('library.tabs.documents'), path: '/user/library/documents', tab: 'documents' },
        { title: t('library.tabs.learning'), path: '/user/library/learning', tab: 'learning' },
        { title: t('library.tabs.surveys'), path: '/user/library/surveys', tab: 'surveys' },
        { title: t('library.tabs.content'), path: '/user/library/content', tab: 'content' },
        { title: t('library.tabs.paths'), path: '/user/library/paths', tab: 'paths' },
      ]
      return filterTabsByPermissions('library', libraryTabs)
    }
    if (isPlanningRoute) {
      const allPlanningTabs = [
        { title: t('planning.tabs.calendar'), path: '/user/planning/attendance-calendar', tab: 'attendance-calendar' },
        { title: t('planning.tabs.pastRequests'), path: '/user/planning/past-requests', tab: 'past-requests' },
        { title: t('planning.tabs.attendance'), path: '/user/planning/attendance', tab: 'attendance' },
        { title: t('planning.tabs.leaveManagement'), path: '/user/planning/leave-management', tab: 'leave-management' },
        { title: t('planning.tabs.workspace'), path: '/user/planning/workspace', tab: 'workspace' },
        { title: t('planning.tabs.timeTracking'), path: '/user/planning/time-tracking', tab: 'time-tracking' },
      ]
      return filterTabsByPermissions('planning', allPlanningTabs)
    }
    if (isPayrollRoute) {
      return [
        { title: t('payroll.tabs.overview'), path: '/user/payroll/people', tab: 'overview' },
        { title: t('payroll.tabs.workingDays'), path: '/user/payroll/working-days', tab: 'working-days' },
        { title: t('payroll.tabs.benefits'), path: '/user/payroll/benefits', tab: 'benefits' },
      ]
    }
    if (isInflowRoute) {
      const inflowTabs = [
        { title: t('inflow.tabs.candidates'), path: '/user/inflow/candidates', tab: 'candidates' },
        { title: t('inflow.tabs.pipeline'), path: '/user/inflow/pipeline', tab: 'pipeline' },
        { title: t('inflow.tabs.interviews'), path: '/user/inflow/interviews', tab: 'interviews' },
        { title: t('inflow.tabs.vacancy'), path: '/user/inflow/vacancy', tab: 'vacancy' },
        { title: t('inflow.tabs.overview'), path: '/user/inflow/overview', tab: 'overview' },
        { title: t('inflow.tabs.onboarding'), path: '/user/inflow/onboarding', tab: 'onboarding' },
      ]
      return filterTabsByPermissions('inflow', inflowTabs)
    }
    if (isOutflowRoute) {
      return [
        { title: t('outflow.tabs.offboarding'), path: '/user/outflow/offboarding', tab: 'offboarding' },
        { title: t('outflow.tabs.exitInterviews'), path: '/user/outflow/exit-interviews', tab: 'exit-interviews' },
        { title: t('outflow.tabs.outstandingAssets'), path: '/user/outflow/outstanding-assets', tab: 'outstanding-assets' },
      ]
    }
    if (isAnalyticsRoute) {
      return [
        { title: t('analytics.tabs.workforce'), path: '/user/analytics/workforce', tab: 'workforce' },
        { title: t('analytics.tabs.attendance'), path: '/user/analytics/attendance', tab: 'attendance' },
        { title: t('analytics.tabs.evaluation'), path: '/user/analytics/evaluation', tab: 'evaluation' },
        { title: t('analytics.tabs.skills'), path: '/user/analytics/skills', tab: 'skills' },
        { title: t('analytics.tabs.compensation'), path: '/user/analytics/compensation', tab: 'compensation' },
        { title: t('analytics.tabs.recruitment'), path: '/user/analytics/recruitment', tab: 'recruitment' },
        { title: t('analytics.tabs.turnover'), path: '/user/analytics/turnover', tab: 'turnover' },
      ]
    }
    if (isFaqRoute) {
      if (isOrgAdmin || isSuperAdmin) {
        return [
          { title: t('faq.tabs.browse'), path: '/user/faq', tab: 'browse' },
          { title: t('faq.tabs.manage'), path: '/user/faq/manage', tab: 'manage' },
        ]
      }
      return null
    }
    if (isSettingsRoute) {
      return [
        { title: t('settings.tabs.general'), path: '/user/settings/general', tab: 'general' },
        { title: t('settings.tabs.modules'), path: '/user/settings/modules', tab: 'modules' },
        ...(hasAssessmentsFeature
          ? [{ title: t('settings.tabs.assessments'), path: '/user/settings/assessments', tab: 'assessments' }]
          : []),
        { title: t('settings.tabs.payments'), path: '/user/settings/payments', tab: 'payments' },
        { title: t('settings.tabs.integration'), path: '/user/settings/integration', tab: 'integration' },
        { title: t('settings.tabs.access'), path: '/user/settings/access', tab: 'access' },
        ...(isSuperAdmin ? [
          { title: t('settings.tabs.organizations'), path: '/user/settings/organizations', tab: 'organizations' },
          { title: t('settings.tabs.plans'), path: '/user/settings/plans', tab: 'plans' },
        ] : []),
      ]
    }
    return null
  }

  const tabConfig = getTabConfig()
  const visibleTabConfig = tabConfig && tabConfig.length > 1 ? tabConfig : null

  const getInitialTab = () => {
    if (isWorkforceRoute) {
      if (location.pathname.includes('teams')) return 'teams'
      if (location.pathname.includes('organization')) return 'organization'
      if (location.pathname.includes('assets')) return 'assets'
      if (location.pathname.includes('mutations')) return 'mutations'
      return 'people'
    }
    if (isClassificationRoute) {
      if (location.pathname.includes('skills')) return 'skills'
      if (location.pathname.includes('competences')) return 'competences'
      if (location.pathname.includes('tasks')) return 'tasks'
      if (location.pathname.includes('roles')) return 'roles'
      if (location.pathname.includes('accountability')) return 'accountability'
      return 'jobprofile'
    }
    if (isOrganizationRoute) {
      if (location.pathname.includes('/user/organization/organization')) return 'organization'
      return 'myTeams'
    }
    if (isExploreRoute) {
      if (location.pathname.includes('content')) return 'explore'
      if (location.pathname.includes('assessments')) return 'assessments'
      if (location.pathname.includes('paths') || location.pathname.includes('userpath')) return 'paths'
    }
    if (isAssignRoute) {
      if (location.pathname.includes('learning')) return 'learning'
      if (location.pathname.includes('survey')) return 'survey'
      if (location.pathname.includes('path')) return 'path'
      if (location.pathname.includes('statistics')) return 'statistics'
      return 'document'
    }
    if (isLibraryRoute) {
      if (location.pathname.includes('learning')) return 'learning'
      if (location.pathname.includes('surveys')) return 'surveys'
      if (location.pathname.includes('content')) return 'content'
      if (location.pathname.includes('paths')) return 'paths'
      return 'documents'
    }
    if (isPayrollRoute) {
      if (location.pathname.includes('benefits')) return 'benefits'
      if (location.pathname.includes('working-days')) return 'working-days'
      return 'overview'
    }
    if (isInflowRoute) {
      if (location.pathname.includes('pipeline')) return 'pipeline'
      if (location.pathname.includes('interviews')) return 'interviews'
      if (location.pathname.includes('vacancy')) return 'vacancy'
      if (location.pathname.includes('overview')) return 'overview'
      if (location.pathname.includes('onboarding')) return 'onboarding'
      return 'candidates'
    }
    if (isOutflowRoute) {
      if (location.pathname.includes('exit-interviews')) return 'exit-interviews'
      if (location.pathname.includes('outstanding-assets')) return 'outstanding-assets'
      return 'offboarding'
    }
    if (isAnalyticsRoute) {
      if (location.pathname.includes('attendance')) return 'attendance'
      if (location.pathname.includes('evaluation')) return 'evaluation'
      if (location.pathname.includes('skills')) return 'skills'
      if (location.pathname.includes('compensation')) return 'compensation'
      if (location.pathname.includes('recruitment')) return 'recruitment'
      if (location.pathname.includes('turnover')) return 'turnover'
      return 'workforce'
    }
    if (isFaqRoute) {
      if (location.pathname.includes('manage')) return 'manage'
      return 'browse'
    }
    if (isSettingsRoute) {
      if (location.pathname.includes('organizations')) return 'organizations'
      if (location.pathname.includes('plans')) return 'plans'
      if (location.pathname.includes('modules')) return 'modules'
      if (location.pathname.includes('payments')) return 'payments'
      if (location.pathname.includes('integration')) return 'integration'
      if (location.pathname.includes('access')) return 'access'
      if (location.pathname.includes('general')) return 'general'
      return 'general'
    }
    if (isPlanningRoute) {
      if (location.pathname.includes('attendance-calendar')) return 'attendance-calendar'
      if (location.pathname.includes('past-requests')) return 'past-requests'
      if (location.pathname.includes('leave-management')) return 'leave-management'
      if (location.pathname.includes('timeline')) return 'timeline'
      if (location.pathname.includes('attendance')) return 'attendance'
      if (location.pathname.includes('workspace')) return 'workspace'
      if (location.pathname.includes('time-tracking')) return 'time-tracking'
      return 'attendance-calendar'
    }
    return 'people'
  }

  const [selectedTab, setSelectedTab] = useState(getInitialTab)
  const [optimisticTab, setOptimisticTab] = useState<string | null>(null)
  const optimisticTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const openSearch = useCallback(() => setSearchOpen(true), [])
  const closeSearch = useCallback(() => setSearchOpen(false), [])

  // Optimistic tab switch: set optimisticTab immediately on click, clear when URL settles
  const handleSelectTab = useCallback((tab: string, path?: string) => {
    setOptimisticTab(tab)
    if (optimisticTimeoutRef.current) {
      clearTimeout(optimisticTimeoutRef.current)
    }
    optimisticTimeoutRef.current = setTimeout(() => {
      setOptimisticTab(null)
    }, 500)
    if (path) {
      navigate(path)
    }
  }, [navigate])

  useEffect(() => {
    if (optimisticTimeoutRef.current) {
      clearTimeout(optimisticTimeoutRef.current)
      optimisticTimeoutRef.current = null
    }
    setOptimisticTab(null)
  }, [location.pathname])

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Use auth user data (already fetched by useAuth) instead of a separate query
  const user = auth?.user
  const { isPending: isCurrentSelfPending } = useCurrentSelf(!!user?.id)

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      localStorage.removeItem('selectedNav')
    }
  }, [auth?.isAuthenticated])

  useEffect(() => {
    setSelectedTab(getInitialTab)
  }, [location.pathname])

  // Auth guards
  if (!auth?.isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }
  const mustCompleteMFA = auth.requiresMFA || (auth.mfaRequiredForUser && !auth.mfaVerifiedThisSession)
  if (mustCompleteMFA) {
    return <Navigate to="/auth/mfa" replace />
  }
  if (isCurrentSelfPending || auth?.loading?.query) {
    return <Loading />
  }
  if (auth.isVerified === false) {
    return <Navigate to="/auth/verification" replace />
  }

  if (isPermissionsLoading && !isOrgAdmin && !isSuperAdmin && isPermissionGuardedRoute) {
    return <Loading />
  }

  if (isWorkforceRoute && !hasWorkforceAccess) {
    return <Navigate to="/user/home" replace />
  }

  if (isInflowRoute && !hasInflowAccess) {
    return <Navigate to="/user/home" replace />
  }

  // A single allowed Hiring tab must not implicitly unlock every other direct
  // /inflow URL. Keep the current PagePermission model intact while the two
  // lifecycle areas share one visual navigation shell.
  if (isInflowRoute && !isOrgAdmin && !isSuperAdmin && !isPermissionsLoading) {
    const allowedTabs = myPermissions?.inflow || []
    const currentTab = location.pathname.includes('/pipeline')
      ? 'pipeline'
      : location.pathname.includes('/interviews')
        ? 'interviews'
        : location.pathname.includes('/vacancy')
          ? 'vacancy'
          : location.pathname.includes('/overview')
            ? 'overview'
            : location.pathname.includes('/onboarding')
              ? 'onboarding'
              : 'candidates'
    if (!allowedTabs.includes(currentTab)) {
      const fallback = [
        ['candidates', '/user/inflow/candidates'],
        ['pipeline', '/user/inflow/pipeline'],
        ['interviews', '/user/inflow/interviews'],
        ['vacancy', '/user/inflow/vacancy'],
        ['overview', '/user/inflow/overview'],
        ['onboarding', '/user/inflow/onboarding'],
      ].find(([tab]) => allowedTabs.includes(tab))
      return <Navigate to={fallback?.[1] || '/user/home'} replace />
    }
  }

  if (isExploreRoute && !hasExploreAccess) {
    return <Navigate to="/user/home" replace />
  }

  if (isLibraryRoute && !hasLibraryAccess) {
    return <Navigate to="/user/home" replace />
  }

  // Redirect to the first tab the user actually has. /user/content/* item
  // pages are left reachable: paths link into them.
  if (isExploreRoute && !isOrgAdmin && !isSuperAdmin) {
    // Legacy 'library' grants map to the merged Content (explore) tab.
    const allowedExploreTabs = (myPermissions?.explore || []).map((tab) => (tab === 'library' ? 'explore' : tab))
    const exploreTabPaths: Record<string, string> = {
      explore: '/user/explore/content',
      paths: isAdmin ? '/user/explore/paths' : '/user/explore/userpath',
      assessments: '/user/assessments',
    }
    const currentExploreTab = location.pathname.startsWith('/user/explore/content')
      ? 'explore'
      : location.pathname.startsWith('/user/explore/paths') || location.pathname.startsWith('/user/explore/userpath')
        ? 'paths'
        : null
    if (currentExploreTab && !allowedExploreTabs.includes(currentExploreTab)) {
      const fallbackTab = ['explore', 'paths', 'assessments'].find((tab) => allowedExploreTabs.includes(tab))
      return <Navigate to={fallbackTab ? exploreTabPaths[fallbackTab] : '/user/home'} replace />
    }
  }

  // Assessments used to be an Explore sub-tab and was guarded by the block
  // above. It has its own route now, so the same 'assessments' grant is checked
  // here — moving a page must not hand it to people who could not open it.
  if (isAssessmentsListRoute && !isOrgAdmin && !isSuperAdmin) {
    const allowedExploreTabs = myPermissions?.explore || []
    if (!isPermissionsLoading && !allowedExploreTabs.includes('assessments')) {
      return <Navigate to="/user/home" replace />
    }
  }

  if (!isSubscriptionLoading && isAssessmentsRouteFamily && !hasAssessmentsFeature) {
    return <Navigate to="/user/home" replace />
  }

  if (!isSubscriptionLoading && location.pathname === '/user/settings/assessments' && !hasAssessmentsFeature) {
    return <Navigate to="/user/settings/general" replace />
  }

  if (isHRChatRoute && !hasHRChatAccess) {
    return <Navigate to="/user/home" replace />
  }

  // Subscription-based route guard: free-plan users can only access home, todo, settings, profile
  if (auth?.isAuthenticated && auth?.isVerified && !isSubscriptionLoading && !hasPaidSubscription && !isFreeRoute) {
    return <Navigate to="/user/settings/payments" replace />
  }

  return (
    <TabNotificationProvider>
      <Flex
        w="100%"
        h={{ base: 'auto', md: '100vh' }}
        minH={{ base: '100vh', md: '100vh' }}
        bg="white"
        overflow={{ base: 'visible', md: 'hidden' }}
      >
        {/* Navbar — hidden on mobile via its own display prop */}
        {showChrome && <NavigationBar />}

        {/* Content area */}
        <Flex
          direction="column"
          flex="1"
          background={CONTENT_AREA_BG}
          borderTopLeftRadius="0"
          overflow={{ base: 'visible', md: 'hidden' }}
          w={{ base: '100%', md: showChrome ? 'calc(100% - 230px)' : '100%' }}
        >
          {/* PageHeader: title + subtitle + user menu + optional tabs */}
          {showChrome && (
            <PageHeader
              tabConfig={visibleTabConfig}
              selectedTab={optimisticTab ?? selectedTab}
              onSelectTab={handleSelectTab}
              onSearchClick={openSearch}
            />
          )}

          {/* Page content */}
          <Box
            flex={{ base: 'none', md: '1' }}
            minH={{ base: 'auto', md: '0' }}
            px={isPathsRoute ? 0 : { base: '16px', md: '32px' }}
            pt={isPathsRoute ? 0 : { base: '8px', md: 0 }}
            pb={isPathsRoute ? 0 : { base: '24px', md: '16px' }}
            overflowX="hidden"
            overflowY={{ base: 'visible', md: isPathsRoute ? 'hidden' : 'auto' }}
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.08)', borderRadius: '2px' },
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,0,0,0.08) transparent',
            }}
          >
            <MotionBox
              key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              h={{ base: 'auto', md: '100%' }}
              minH="100%"
            >
              {React.cloneElement(children as React.ReactElement, { selectedTab, optimisticTab })}
            </MotionBox>
          </Box>
        </Flex>

        <GlobalSearch isOpen={searchOpen} onClose={closeSearch} />
        {hasHRChatAccess && <HRChatWidget />}
      </Flex>
    </TabNotificationProvider>
  )
}

export default UserWrapper
